'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import Divider from '@mui/material/Divider';
import Stack, { StackProps } from '@mui/material/Stack';
import Button, { ButtonProps } from '@mui/material/Button';
import Popover, { PopoverProps } from '@mui/material/Popover';

import {
  SessionContext,
  AuthenticationContext,
} from '../AppProvider/AppProvider';
import {
  useLocaleText,
  type LocaleText,
} from '../AppProvider/LocalizationProvider';

import { SignInButton } from './SignInButton';
import { SignOutButton } from './SignOutButton';
import { AccountPopoverHeader } from './AccountPopoverHeader';
import { AccountPopoverFooter } from './AccountPopoverFooter';
import { AccountLocaleContext } from './AccountLocaleContext';
import { AccountPreview, AccountPreviewProps } from './AccountPreview';

interface AccountLocaleText {
  accountSignInLabel: string;
  accountSignOutLabel: string;

  accountPreviewTitle: string;
  accountPreviewIconButtonLabel: string;
}

export interface AccountSlots {
  /**
   * The component used for the account popover menu
   * @default Popover
   */
  popover?: React.JSXElementConstructor<PopoverProps>;
  /**
   * The component used for the sign in button.
   * @default Button
   */
  signInButton?: React.JSXElementConstructor<ButtonProps>;
  /**
   * The component used for the content of account popover
   * @default Stack
   */
  popoverContent?: React.JSXElementConstructor<StackProps>;
  /**
   * The component used for the sign out button.
   * @default Button
   */
  signOutButton?: React.JSXElementConstructor<ButtonProps>;
  /**
   * The component used for the account preview
   * @default AccountPreview
   */
  preview?: React.JSXElementConstructor<AccountPreviewProps>;
}

export interface AccountProps {
  /**
   * The components used for each slot inside.
   */
  slots?: AccountSlots;
  /**
   * The labels for the account component.
   */
  localeText?: Partial<AccountLocaleText>;
  /**
   * The props used for each slot inside.
   */
  slotProps?: {
    preview?: AccountPreviewProps;
    popoverContent?: React.ComponentProps<typeof Stack>;
    signOutButton?: React.ComponentProps<typeof Button>;
    signInButton?: React.ComponentProps<typeof SignInButton>;
    popover?: Omit<React.ComponentProps<typeof Popover>, 'open'>;
  };
}

const defaultAccountLocaleText: Pick<LocaleText, keyof AccountLocaleText> = {
  accountSignInLabel: 'Sign in',
  accountPreviewTitle: 'Account',
  accountSignOutLabel: 'Sign out',
  accountPreviewIconButtonLabel: 'Current User',
};

/**
 *
 * Demos:
 *
 * - [Account](https://mui.com/toolpad/core/react-account/)
 * - [Dashboard Layout](https://mui.com/toolpad/core/react-dashboard-layout/)
 * - [Sign-in Page](https://mui.com/toolpad/core/react-sign-in-page/)
 *
 * API:
 *
 * - [Account API](https://mui.com/toolpad/core/api/account)
 */
function Account(props: AccountProps) {
  const { localeText: propsLocaleText } = props;
  const globalLocaleText = useLocaleText();
  const localeText = React.useMemo(
    () => ({
      ...defaultAccountLocaleText,
      ...globalLocaleText,
      ...propsLocaleText,
    }),
    [globalLocaleText, propsLocaleText]
  );
  const { slots, slotProps } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const session = React.useContext(SessionContext);
  const authentication = React.useContext(AuthenticationContext);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!authentication) {
    return null;
  }

  let accountContent;

  if (!session?.user) {
    accountContent = slots?.signInButton ? (
      <slots.signInButton onClick={authentication.signIn} />
    ) : (
      <SignInButton {...slotProps?.signInButton} />
    );
  } else {
    accountContent = (
      <React.Fragment>
        {slots?.preview ? (
          <slots.preview open={open} handleClick={handleClick} />
        ) : (
          <AccountPreview
            open={open}
            variant="condensed"
            handleClick={handleClick}
            {...slotProps?.preview}
          />
        )}
        {slots?.popover ? (
          <slots.popover
            open={open}
            onClick={handleClick}
            onClose={handleClose}
            {...slotProps?.popover}
          />
        ) : (
          <Popover
            open={open}
            id="account-menu"
            anchorEl={anchorEl}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            {...slotProps?.popover}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  mt: 1,
                  overflow: 'visible',
                  filter: (theme) =>
                    `drop-shadow(0px 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.32)'})`,
                  '&::before': {
                    top: 0,
                    right: 14,
                    width: 10,
                    zIndex: 0,
                    height: 10,
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                  },
                },
              },
              ...slotProps?.popover?.slotProps,
            }}
          >
            {slots?.popoverContent ? (
              <slots.popoverContent {...slotProps?.popoverContent} />
            ) : (
              <Stack direction="column" {...slotProps?.popoverContent}>
                <AccountPopoverHeader>
                  <AccountPreview variant="expanded" />
                </AccountPopoverHeader>
                <Divider />
                <AccountPopoverFooter>
                  <SignOutButton {...slotProps?.signOutButton} />
                </AccountPopoverFooter>
              </Stack>
            )}
          </Popover>
        )}
      </React.Fragment>
    );
  }

  return (
    <AccountLocaleContext.Provider value={localeText}>
      {accountContent}
    </AccountLocaleContext.Provider>
  );
}

Account.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * The labels for the account component.
   */
  localeText: PropTypes.object,
  /**
   * The components used for each slot inside.
   */
  slots: PropTypes.shape({
    popover: PropTypes.elementType,
    preview: PropTypes.elementType,
    signInButton: PropTypes.elementType,
    signOutButton: PropTypes.elementType,
    popoverContent: PropTypes.elementType,
  }),
  /**
   * The props used for each slot inside.
   */
  slotProps: PropTypes.shape({
    popover: PropTypes.object,
    signInButton: PropTypes.object,
    signOutButton: PropTypes.object,
    popoverContent: PropTypes.object,
    preview: PropTypes.shape({
      open: PropTypes.bool,
      handleClick: PropTypes.func,
      variant: PropTypes.oneOf(['condensed', 'expanded']),
      slotProps: PropTypes.shape({
        avatar: PropTypes.object,
        moreIconButton: PropTypes.object,
        avatarIconButton: PropTypes.object,
      }),
      slots: PropTypes.shape({
        avatar: PropTypes.elementType,
        moreIconButton: PropTypes.elementType,
        avatarIconButton: PropTypes.elementType,
      }),
      sx: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.object,
            PropTypes.bool,
          ])
        ),
        PropTypes.func,
        PropTypes.object,
      ]),
    }),
  }),
} as any;

export { Account };
